//* Import needed libraries
import { useEffect, useRef, useState, useCallback } from "react";
//* Import parseHtmlToText e DOMPurify para sanitizar e converter HTML em texto simples
import { parseHtmlToText } from "../../../utils/questions/questionParseText";
import DOMPurify from "dompurify";

//* Import needed components from react-bootstrap
import { Form, Button, Spinner, Alert } from "react-bootstrap";

//* Import needed hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Import needed icons
import { SaveRounded } from "@material-ui/icons";
import { RiArrowGoBackLine } from "react-icons/ri";

//! Import custom hooks
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";
import { useQuestions } from "../../../hooks/questions/useQuestions";
import { useTests } from "../../../hooks/test/useTests";
import { useQuestionTest } from "../../../hooks/questionsTest/useQuestionTest";

//? Import types
import { Discipline, TestModel } from "./types/types";
import {
  QuestionDetails,
  QuestionType,
} from "../../../hooks/questions/types/types";
import { QuestionItem } from "./types/types";

//! Import custom components
import ExamFormFields from "./ExamFormFields";
import ExamQuestionsSection from "./ExamQuestionsSection";
import ExamSelectedQuestions from "./ExamSelectedQuestions";
import { ExamModalTag } from "../../ExamModalTag";

//* Import debounce
var debounce = require("lodash.debounce");

// Defina um tipo estendido que inclui a propriedade originalType
export type QuestionDetailsWithOriginalType = QuestionDetails & {
  originalType: QuestionType;
};

export function ExamForm({ editMode }: { editMode?: boolean }): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [model, setModel] = useState<TestModel>({
    name: "",
    description: "",
    type: "objective",
    questions: [],
  });
  const [originalQuestions, setOriginalQuestions] = useState<QuestionDetails[]>(
    []
  );
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [loadingScreen, setLoadingScreen] = useState<boolean>(false);
  const [questionsArray, setQuestionsArray] = useState<QuestionItem[]>([]);
  const [loadingInput, setLoadingInput] = useState<boolean>(false);

  const [disciplineSelect, setDisciplineSelect] = useState<Discipline>({
    id: "",
    name: "",
  });
  const [questionContent, setQuestionContent] = useState<string>("");
  const [showAutoCompleteQuestion, setShowAutoCompleteQuestion] =
    useState<boolean>(false);
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [showModalTag, setShowModalTag] = useState<boolean>(false);

  // O estado questions agora é do tipo QuestionDetailsWithOriginalType[]
  const [questions, setQuestions] = useState<QuestionDetailsWithOriginalType[]>(
    []
  );
  const [availableQuestions, setAvailableQuestions] = useState<
    QuestionDetails[]
  >([]);

  // Crie um ref para acumular as questões (não será sobrescrito quando o tipo mudar)
  const accumulatedQuestionsRef = useRef<QuestionDetailsWithOriginalType[]>([]);

  const timeout_ = useRef<NodeJS.Timeout | undefined>(undefined);
  const [edit, setEdit] = useState<boolean>(false);

  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const token = localStorage.getItem("token") || "";

  const {
    discipline: fetchedDisciplines,
    loading: disciplinesLoading,
    error: disciplinesError,
  } = useDisciplines(token);
  const { fetchQuestionsByDiscipline } = useQuestions(token);
  const { getTestById, createTests, updateTests } = useTests(token);
  const { createRelations, deleteRelations } = useQuestionTest(token);
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);

  useEffect(() => {
    if (fetchedDisciplines) {
      setDisciplines(fetchedDisciplines);
    }
  }, [fetchedDisciplines]);

  // Busca os detalhes da prova (modo de edição)
  useEffect(() => {
    const fetchTestDetails = async (id: string) => {
      setLoadingScreen(true);
      try {
        const exam = await getTestById(id);
        if (exam) {
          // Mapeia as questões e grava o originalType usando o tipo da prova atual
          const formattedQuestions: QuestionDetailsWithOriginalType[] =
            exam.questions.map((q: any) => ({
              id: q.questionTestId,
              educationLevel: q.educationLevel,
              difficulty: q.difficulty,
              statement: parseHtmlToText(
                q.statement,
                DOMPurify.sanitize
              ) as string,
              discipline: q.discipline,
              subQuestions: q.subQuestions || [],
              tags: q.tags || [],
              createdAt: new Date(q.createdAt),
              updatedAt: new Date(q.updatedAt),
              createdBy: q.createdBy,
              originalType: q.type || exam.type,
            }));

          setModel({
            id: exam.id,
            name: exam.name,
            description: exam.description || "",
            type:
              exam.type === "objective" || exam.type === "dissertative"
                ? exam.type
                : "objective",
            questions: formattedQuestions,
          });

          // Atualize o estado e a variável acumulada
          setQuestions(formattedQuestions);
          accumulatedQuestionsRef.current = formattedQuestions;
          setOriginalQuestions([...formattedQuestions]);
        }
      } catch (error: any) {
        setErrorMessage(error.message || "Erro ao buscar detalhes da prova.");
      } finally {
        setLoadingScreen(false);
      }
    };

    if (params.id && editMode) {
      setEdit(true);
      fetchTestDetails(params.id);
    } else {
      setLoadingScreen(false);
    }
  }, [params.id, getTestById, editMode]);

  // Define o tipo da prova
  const examType = model.type; // "objective" ou "dissertative"

  // ---------- NOVA LÓGICA: Filtra as questões já selecionadas quando o examType muda ----------
  useEffect(() => {
    // Se a prova for objetiva, mantenha questões do tipo "objective" e "set".
    // Se for dissertativa, mantenha somente "dissertative".
    const validTypes =
      examType === "objective" ? ["objective", "set"] : ["dissertative"];
    const filteredQuestions = accumulatedQuestionsRef.current.filter((q) =>
      validTypes.includes(q.originalType)
    );
    accumulatedQuestionsRef.current = filteredQuestions;
    setQuestions(filteredQuestions);
  }, [examType]);
  // ------------------------------------------------------------------------------------------

  // Função para adicionar uma questão à variável acumulada e atualizar o estado
  const handleAddQuestion = (question: QuestionDetails) => {
    const newQuestion: QuestionDetailsWithOriginalType = {
      ...question,
      originalType: examType,
    };
    accumulatedQuestionsRef.current = [
      ...accumulatedQuestionsRef.current,
      newQuestion,
    ];

    setQuestions(accumulatedQuestionsRef.current);
  };

  // Função para remover uma questão do acumulador e atualizar o estado
  const handleRemoveQuestion = (questionId: string) => {
    // Remove do acumulador
    accumulatedQuestionsRef.current = accumulatedQuestionsRef.current.filter(
      (q) => q.id !== questionId
    );
    // Atualiza o estado para refletir a mudança
    setQuestions(accumulatedQuestionsRef.current);
  };

  // Função debounced para buscar questões baseada no tipo da prova
  const debouncedFetchQuestions = useCallback(
    debounce(async () => {
      if (!disciplineSelect.id) {
        setAvailableQuestions([]);
        setLoadingInput(false);
        setLoadingSearch(false);
        return;
      }
      setLoadingInput(true);
      setLoadingSearch(true);
      try {
        let questionsCombined: QuestionDetails[] = [];
        if (examType === "objective") {
          // Para prova objetiva, buscamos questões do tipo "objective" e "set" (texto em comun)
          const resObjective = await fetchQuestionsByDiscipline(
            disciplineSelect.id,
            "objective",
            1,
            3000,
            questionContent || undefined
          );
          const resSet = await fetchQuestionsByDiscipline(
            disciplineSelect.id,
            "set",
            1,
            3000,
            questionContent || undefined
          );
          const questionsObjective: QuestionDetails[] =
            resObjective && resObjective.questions
              ? resObjective.questions
              : [];
          const questionsSet: QuestionDetails[] =
            resSet && resSet.questions ? resSet.questions : [];
          questionsCombined = [...questionsObjective, ...questionsSet];
        } else if (examType === "dissertative") {
          // Para prova dissertativa, buscamos apenas questões "dissertative"
          const resDissertative = await fetchQuestionsByDiscipline(
            disciplineSelect.id,
            "dissertative",
            1,
            3000,
            questionContent || undefined
          );
          questionsCombined =
            resDissertative && resDissertative.questions
              ? resDissertative.questions
              : [];
        }

        const questionsCleaned: QuestionDetails[] = questionsCombined.map(
          (q: any) => ({
            ...q,
            statement: parseHtmlToText(q.statement, DOMPurify.sanitize),
          })
        );
        setAvailableQuestions(questionsCleaned);
      } catch (error: any) {
        setErrorMessage(error.message || "Erro ao buscar questões.");
      } finally {
        setLoadingInput(false);
        setLoadingSearch(false);
      }
    }, 600),
    [disciplineSelect.id, examType, questionContent, fetchQuestionsByDiscipline]
  );

  // Dispara a busca sempre que a disciplina ou o examType mudar
  useEffect(() => {
    if (!disciplineSelect.id) {
      setAvailableQuestions([]);
      setLoadingInput(false);
      setLoadingSearch(false);
      return;
    }
    debouncedFetchQuestions();
    return () => {
      debouncedFetchQuestions.cancel();
    };
  }, [debouncedFetchQuestions, disciplineSelect.id, examType]);

  // Filtra as questões disponíveis com base no termo pesquisado
  const filteredQuestionsSearch = questionContent
    ? availableQuestions.filter((q) => {
        const mainText = String(q.statement ?? "").trim();
        const subText =
          q.subQuestions && q.subQuestions.length > 0
            ? q.subQuestions.map((sq) => String(sq.statement ?? "")).join(" ")
            : "";
        const combinedText =
          mainText === "" ? subText : mainText + " " + subText;
        return combinedText
          .toLowerCase()
          .includes(questionContent.toLowerCase());
      })
    : [];

  // Converte as questões para o formato do TableDrag
  const parseQuestionsForDragDrop = () => {
    const updatedQuestionsArray: QuestionItem[] = questions.map(
      (item, index) => {
        let tipoQuestaoText = "";
        let enunciadoContent: React.ReactElement = (
          <p className="text_overflow">{String(item.statement ?? "")}</p>
        );

        if (item.subQuestions && item.subQuestions.length > 0) {
          const types = item.subQuestions.map((sq) => sq.type);
          const uniqueTypes = Array.from(new Set(types));
          if (uniqueTypes.length === 1) {
            if (uniqueTypes[0] === "objective") {
              tipoQuestaoText = "Objetiva";
              const objectiveSub = item.subQuestions.find(
                (sq) => sq.type === "objective"
              );
              if (objectiveSub && objectiveSub.statement) {
                enunciadoContent = (
                  <p className="text_overflow">
                    {String(
                      parseHtmlToText(
                        String(objectiveSub.statement),
                        DOMPurify.sanitize
                      )
                    )}
                  </p>
                );
              }
            } else if (uniqueTypes[0] === "dissertative") {
              tipoQuestaoText = "Dissertativa";
              const dissertativeSub = item.subQuestions.find(
                (sq) => sq.type === "dissertative"
              );
              if (dissertativeSub && dissertativeSub.statement) {
                enunciadoContent = (
                  <p className="text_overflow">
                    {String(
                      parseHtmlToText(
                        String(dissertativeSub.statement),
                        DOMPurify.sanitize
                      )
                    )}
                  </p>
                );
              }
            } else {
              tipoQuestaoText = "Texto em Comun";
            }
          } else {
            tipoQuestaoText = "Texto em Comun";
            const dissertativeSub = item.subQuestions.find(
              (sq) => sq.type === "dissertative"
            );
            if (dissertativeSub && dissertativeSub.statement) {
              enunciadoContent = (
                <p className="text_overflow">
                  {String(
                    parseHtmlToText(
                      String(dissertativeSub.statement),
                      DOMPurify.sanitize
                    )
                  )}
                </p>
              );
            } else {
              enunciadoContent = (
                <p className="text_overflow">
                  {String(
                    parseHtmlToText(
                      String(item.subQuestions[0].statement),
                      DOMPurify.sanitize
                    )
                  )}
                </p>
              );
            }
          }
        }

        const disciplineName = item.discipline?.name || "Sem disciplina";

        return {
          numero: index + 1,
          id: item.id,
          index: index,
          enunciado: enunciadoContent,
          disciplina: disciplineName,
          tipoQuestao: <p style={{ whiteSpace: "pre" }}>{tipoQuestaoText}</p>,
          acoes: index,
        };
      }
    );
    setQuestionsArray(updatedQuestionsArray);
  };

  useEffect(() => {
    parseQuestionsForDragDrop();
  }, [questions, disciplines]);

  // Ao salvar, usamos o acumulador (accumulatedQuestionsRef)
  const saveContent = async () => {
    if (model.name.length < 3) {
      setErrorMessage("O nome da prova deve ter pelo menos 3 caracteres.");
      return;
    }
    setLoadingSave(true);
    try {
      // Envia todas as questões acumuladas
      const questionsToSend = accumulatedQuestionsRef.current;
      const updatedModel: TestModel = {
        ...model,
        questions: questionsToSend,
      };

      let createdTestId: string | undefined = updatedModel.id;


      if (edit && updatedModel.id) {
        await updateTests({ id: updatedModel.id, testData: updatedModel });
        createdTestId = updatedModel.id;
      } else {
        const createdTest = await createTests(updatedModel);
        if (!createdTest || !createdTest.id) {
          throw new Error("Falha ao criar o teste.");
        }
        createdTestId = createdTest.id;
      }

      if (createdTestId) {
        if (edit) {
          const originalIds = originalQuestions.map((q) => q.id);
          const newIds = questionsToSend.map((q) => q.id);

          const addedQuestions = questionsToSend.filter(
            (q) => !originalIds.includes(q.id)
          );
          const removedQuestions = originalQuestions.filter(
            (q) => !newIds.includes(q.id)
          );

          if (addedQuestions.length > 0) {
            const relations = addedQuestions.map((q) => ({
              test_id: createdTestId,
              question_id: q.id,
            }));
            await createRelations({ relations });
          }
          if (removedQuestions.length > 0) {
            const questionTestIdsToDelete = removedQuestions.map((q) => q.id);
            if (questionTestIdsToDelete.length > 0) {
              await deleteRelations(questionTestIdsToDelete);
            }
          }
        } else {
          const relations = questionsToSend.map((q) => ({
            test_id: createdTestId,
            question_id: q.id,
          }));
          await createRelations({ relations });
        }
      }
      navigate("/exams");
    } catch (error: any) {
      setErrorMessage(error.message || "Erro ao salvar a prova.");
    } finally {
      setLoadingSave(false);
    }
  };

  const isNameValid = model.name.length >= 3;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card" style={{ overflow: "visible" }}>
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              {edit ? "Editar" : "Cadastrar"} Prova
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              {edit
                ? "Edite uma prova do seu sistema"
                : "Cadastre as provas do seu sistema"}
            </p>
            <br />
            {!loadingScreen && (
              <div>
                {errorMessage && (
                  <div
                    style={{ display: "flex", marginBottom: 15, width: "100%" }}
                  >
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setErrorMessage(null)}
                      className="w-100"
                    >
                      <p style={{ marginBottom: 0, whiteSpace: "pre-line" }}>
                        {errorMessage}
                      </p>
                    </Alert>
                  </div>
                )}
                {disciplinesError && (
                  <Alert variant="danger" role="alert">
                    Erro ao carregar disciplinas: {disciplinesError}
                  </Alert>
                )}
                {disciplinesLoading ? (
                  <div className="d-flex justify-content-center my-3">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveContent();
                    }}
                    style={{ position: "relative" }}
                  >
                    <ExamFormFields
                      model={model}
                      setModel={setModel}
                      loadingInput={loadingInput}
                      paramsId={params.id}
                      editMode={edit}
                    />
                    <ExamQuestionsSection
                      disciplines={disciplines}
                      disciplineSelect={disciplineSelect}
                      setDisciplineSelect={setDisciplineSelect}
                      loadingInput={loadingInput}
                      questionContent={questionContent}
                      setQuestionContent={setQuestionContent}
                      showAutoCompleteQuestion={showAutoCompleteQuestion}
                      setShowAutoCompleteQuestion={setShowAutoCompleteQuestion}
                      loadingSearch={loadingSearch}
                      setLoadingSearch={setLoadingSearch}
                      filteredQuestionsSearch={filteredQuestionsSearch}
                      questions={questions}
                      setQuestions={setQuestions}
                      availableQuestions={availableQuestions}
                      setShowModalTag={setShowModalTag}
                      timeout_={timeout_}
                      examType={examType}
                      editMode={edit}
                      parseHtmlToText={parseHtmlToText}
                      DOMPurify={DOMPurify}
                      handleAddQuestion={handleAddQuestion}
                      handleRemoveQuestion={handleRemoveQuestion}
                    />
                    <ExamSelectedQuestions
                      questionsArray={questionsArray}
                      setQuestionsArray={setQuestionsArray}
                      questions={questions}
                      setQuestions={setQuestions}
                      token={token}
                      editMode={edit}
                      accumulatedQuestionsRef={accumulatedQuestionsRef}
                    />
                    <ExamModalTag
                      showModalTag={showModalTag}
                      onClose={() => setShowModalTag(false)}
                      disciplineId={disciplineSelect.id}
                      disciplineName={disciplineSelect.name}
                      token={token}
                      examType={examType}
                      onSelectQuestions={(
                        selectedQuestions: QuestionDetails[]
                      ) => {
                        selectedQuestions.forEach((q) => {
                          if (
                            !questions.find((existing) => existing.id === q.id)
                          ) {
                            handleAddQuestion(q);
                          }
                        });
                      }}
                    />
                    {!loadingSave && (
                      <div className="w100 inline-flex justify-content-between mt-3">
                        <Button
                          variant="secondary"
                          className="d-flex align-items-center"
                          onClick={() => navigate(-1)}
                        >
                          <RiArrowGoBackLine style={{ marginRight: 10 }} />{" "}
                          Voltar
                        </Button>
                        <Button
                          variant="success"
                          type="submit"
                          disabled={!isNameValid || loadingSave}
                          className="d-flex align-items-center"
                        >
                          <SaveRounded style={{ marginRight: 10 }} /> Salvar
                        </Button>
                      </div>
                    )}
                    {loadingSave && (
                      <div className="d-flex justify-content-center align-items-center my-3">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    )}
                  </Form>
                )}
              </div>
            )}
            {loadingScreen && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ marginTop: "2rem" }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExamForm;
